// src/hooks/useUserMarketPosition.js
import { useAccount } from "wagmi";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch user's position in a specific InfoFi market from backend API
 * @param {number|string} marketId - Market ID
 * @param {Object} [options]
 * @param {boolean} [options.isLive=true] - When false, stops polling (settled markets)
 * @returns {Object} Query result with position data
 */
export const useUserMarketPosition = (marketId, { isLive = true } = {}) => {
  const { address } = useAccount();

  return useQuery({
    queryKey: ["userMarketPosition", marketId, address],
    enabled: !!address && !!marketId,
    queryFn: async () => {
      const apiUrl =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api";
      const response = await fetch(
        `${apiUrl}/infofi/positions/${address}/net?marketId=${marketId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user position");
      }

      const data = await response.json();

      // Backend returns: { yes, no, net, isHedged, numTradesYes, numTradesNo }
      return {
        yesAmount: BigInt(data.yes || 0),
        noAmount: BigInt(data.no || 0),
        netPosition: BigInt(data.net || 0),
        isHedged: data.isHedged || false,
        numTradesYes: data.numTradesYes || 0,
        numTradesNo: data.numTradesNo || 0,
      };
    },
    staleTime: isLive ? 10_000 : Infinity,
    refetchInterval: isLive ? 15_000 : false,
  });
};

/**
 * Hook to fetch market info (pools, volume) from backend API
 * @param {number|string} marketId - Market ID
 * @param {Object} [options]
 * @param {boolean} [options.isLive=true] - When false, stops polling (settled markets)
 * @returns {Object} Query result with market info
 */
export const useMarketInfo = (marketId, { isLive = true } = {}) => {
  return useQuery({
    queryKey: ["marketInfo", marketId],
    enabled: !!marketId,
    queryFn: async () => {
      const apiUrl =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api";
      const response = await fetch(`${apiUrl}/infofi/markets/${marketId}/info`);

      if (!response.ok) {
        // If endpoint doesn't exist yet, return default values
        return {
          totalYesPool: 0n,
          totalNoPool: 0n,
          volume: 0n,
        };
      }

      const data = await response.json();

      return {
        totalYesPool: BigInt(data.totalYesPool || 0),
        totalNoPool: BigInt(data.totalNoPool || 0),
        volume: BigInt(data.volume || 0),
      };
    },
    staleTime: isLive ? 15_000 : Infinity,
    refetchInterval: isLive ? 20_000 : false,
  });
};
